export const MAX_URL_LENGTH = 2048;

/** UUID for Idempotency-Key; use crypto.randomUUID when available. */
export function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const MIN_DEPTH = 1;
const MAX_DEPTH = 20;
export const DEFAULT_DEPTH = 10;
const MIN_PAGE_LIMIT = 1;
const MAX_PAGE_LIMIT = 10000;
export const DEFAULT_PAGE_LIMIT = 500;

/**
 * @param {string} raw
 * @returns {string} Empty string if valid, otherwise a short error message for the field.
 */
export function getUrlValidationError(raw) {
  const value = (raw || '').trim();
  if (!value) {
    return 'Please enter a website URL.';
  }
  if (value.length > MAX_URL_LENGTH) {
    return `URL must be at most ${MAX_URL_LENGTH} characters.`;
  }
  try {
    const u = new URL(value);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') {
      return 'Please use http:// or https://.';
    }
    if (!u.hostname) {
      return 'Please enter a valid URL (e.g. https://example.com).';
    }
    return '';
  } catch (e) {
    return 'Please enter a valid URL (e.g. https://example.com).';
  }
}

/**
 * Normalizes a URL for duplicate comparison, aligned with backend (production)
 * behavior:
 *  - Lowercases scheme + host (case-insensitive per spec).
 *  - Lowercases pathname (production normalizes path case).
 *  - Decodes percent-encoding in the path (%2B → +).
 *  - Strips fragment (#section removed).
 *  - Preserves trailing slash exactly as the user typed it (new URL() auto-
 *    appends "/" to bare origins; we undo that when the input didn't have one).
 *  - Preserves default ports (:443 / :80) when explicitly present in the input
 *    (new URL() silently strips them).
 *  - Preserves query param order and userinfo as-is.
 * Falls back to trimmed lowercase if the URL can't be parsed.
 */
export function normalizeUrlForDedup(raw) {
  const trimmed = (raw || '').trim();
  try {
    const u = new URL(trimmed);
    u.hash = '';

    const inputHadTrailingSlash = /^[^?#]*\/$/.test(trimmed.replace(/^https?:\/\/[^/?#]+/, ''));
    const decodedPath = decodeURIComponent(u.pathname).toLowerCase();
    const path = !inputHadTrailingSlash && decodedPath === '/' ? '' : decodedPath;

    const defaultPortRe = /^https?:\/\/[^/?#]+:(\d+)/;
    const portMatch = trimmed.match(defaultPortRe);
    const explicitPort = portMatch ? ':' + portMatch[1] : '';
    const hostPort = u.hostname + (explicitPort || (u.port ? ':' + u.port : ''));

    return u.protocol + '//' + (u.username ? u.username + (u.password ? ':' + u.password : '') + '@' : '') + hostPort + path + u.search;
  } catch {
    return trimmed.toLowerCase();
  }
}

/**
 * Parses bulk URL input. URLs must be separated by newlines only (commas and spaces are not treated as delimiters).
 * @param {string} text
 * @returns {{ valid: string[], invalid: number, duplicates: number }}
 */

export function parseBulkUrlInput(text) {
  const lines = (text || '').split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const seen = new Set();
  let duplicates = 0;
  let invalid = 0;
  const valid = [];
  lines.forEach((url) => {
    const normalized = normalizeUrlForDedup(url);
    if (seen.has(normalized)) {
      duplicates += 1;
      return;
    }
    seen.add(normalized);
    const err = getUrlValidationError(url);
    if (err) {
      invalid += 1;
    } else {
      valid.push(url.trim());
    }
  });
  return { valid, invalid, duplicates };
}

/**
 * @param {*} value
 * @returns {number}
 */
export function clampCrawlDepth(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return DEFAULT_DEPTH;
  }
  return Math.min(MAX_DEPTH, Math.max(MIN_DEPTH, Math.round(n)));
}

/**
 * @param {*} value
 * @returns {number}
 */
export function clampPageLimit(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return DEFAULT_PAGE_LIMIT;
  }
  return Math.min(MAX_PAGE_LIMIT, Math.max(MIN_PAGE_LIMIT, Math.round(n)));
}
